import React from 'react'
import AntdTypography from 'antd/es/typography'
import AntdLink from 'antd/es/typography/Link'
import type { LinkProps } from 'antd/es/typography/Link'
import AntdParagraph from 'antd/es/typography/Paragraph'
import type { ParagraphProps } from 'antd/es/typography/Paragraph'
import AntdText from 'antd/es/typography/Text'
import type { TextProps } from 'antd/es/typography/Text'
import AntdTitle from 'antd/es/typography/Title'
import type { TitleProps } from 'antd/es/typography/Title'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import {
  Typography as ShadcnSeamTypography,
  Text as ShadcnText,
  Title as ShadcnTitle,
  Paragraph as ShadcnParagraph,
  Link as ShadcnLink,
} from './Typography.shadcn'

type TypographyRootProps = React.ComponentProps<typeof AntdTypography>

/**
 * Compat seam leaf for antd's `antd/es/typography` — and, with `./Space`, the
 * SECOND and THIRD components put behind the design toggle (after `./Button`).
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it, and the call sites for this module are
 * boot-static. `update-design` ships a real seam at this same path, with the
 * same shape (named `Typography`/`Text`/`Title`/`Paragraph`/`Link` plus a
 * default `Typography`), so a redirected call site needs no further change
 * when that lands. The seam leaves mirror the merge target per module, so
 * which of the two shapes a leaf carries differs on purpose:
 * `Button`/`Result`/`Skeleton` are named-only there.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch each dispatcher below renders
 * antd's own component with the props it was given and the ref it was given:
 * same element, same class names, same DOM. The only difference from the
 * `export { default as … } from 'antd/es/typography/…'` lines this file used
 * to be is one extra component in the React tree, which renders nothing of its
 * own. `__tests__/typography-seam-parity.test.tsx` asserts that byte for byte
 * across a prop matrix, and it is the test to keep green: `development`
 * continuing to ship antd unchanged to customers is the whole premise of
 * shipping shadcn one component at a time.
 *
 * WHY TYPOGRAPHY WAS CHOSEN SECOND. It is the widest surface in the seam —
 * 478 files import it and the app's own `src/storybook/components/Typography`
 * (another ~430 call sites) renders it underneath, so flipping the control
 * restyles essentially every word on screen and a half-working switch is
 * impossible to miss. It carries no popup and no portal of its own, and the
 * reference branch's visual-defect survey records no open defect against it.
 * The one portal it can reach — the `ellipsis={{ tooltip }}` hover — is
 * deliberately left on antd's Tooltip (see `./Tooltip.tsx`) so this port
 * changes type, and only type.
 *
 * antd's four statics are carried across explicitly, NOT by copying antd's
 * component object: `Object.assign(dispatcher, AntdTypography)` would also
 * copy a forwardRef object's `$$typeof` and `render` and turn this into
 * something React cannot render. Each static dispatches on the mode exactly as
 * the root does, so the seam cannot end up half-switched — and it must, since
 * `src/storybook/components/Typography` destructures them at module scope.
 * ------------------------------------------------------------------------- */
const TypographyDispatcher = React.forwardRef<HTMLElement, TypographyRootProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') {
      return (
        <ShadcnSeamTypography
          {...props}
          ref={ref as React.Ref<HTMLDivElement>}
        />
      )
    }

    return <AntdTypography {...props} ref={ref} />
  }
)

TypographyDispatcher.displayName = 'Typography'

const TextDispatcher = React.forwardRef<HTMLSpanElement, TextProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') return <ShadcnText {...props} ref={ref} />

    return <AntdText {...props} ref={ref} />
  }
)

TextDispatcher.displayName = 'Typography.Text'

const TitleDispatcher = React.forwardRef<HTMLHeadingElement, TitleProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') return <ShadcnTitle {...props} ref={ref} />

    return <AntdTitle {...props} ref={ref} />
  }
)

TitleDispatcher.displayName = 'Typography.Title'

const ParagraphDispatcher = React.forwardRef<HTMLElement, ParagraphProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') return <ShadcnParagraph {...props} ref={ref} />

    return <AntdParagraph {...props} ref={ref} />
  }
)

ParagraphDispatcher.displayName = 'Typography.Paragraph'

const LinkDispatcher = React.forwardRef<HTMLElement, LinkProps>(
  (props, ref) => {
    const designMode = useDesignMode()

    if (designMode === 'shadcn') return <ShadcnLink {...props} ref={ref} />

    return <AntdLink {...props} ref={ref} />
  }
)

LinkDispatcher.displayName = 'Typography.Link'

export const Typography = Object.assign(TypographyDispatcher, {
  Text: TextDispatcher,
  Title: TitleDispatcher,
  Paragraph: ParagraphDispatcher,
  Link: LinkDispatcher,
})

export const Text = TextDispatcher
export const Title = TitleDispatcher
export const Paragraph = ParagraphDispatcher
export const Link = LinkDispatcher

export default Typography
